import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CalendarDays, CheckCircle2, Clock3, Flag, RefreshCcw, Target, Users } from 'lucide-react';
import { useAuth } from '../Auth';
import { apiService } from '../../services/api';
import { PROJECT_PRIORITY_OPTIONS as PRIORITY_OPTIONS, getProjectPriorityInfo as getPriorityInfo } from '../../utils/projectPriority';

const STATUS_OPTIONS = [
  { value: 'achieved', label: 'Atteint', className: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  { value: 'partial', label: 'Partiel', className: 'bg-amber-50 text-amber-700 border-amber-100' },
  { value: 'missed', label: 'Non atteint', className: 'bg-rose-50 text-rose-700 border-rose-100' },
  { value: 'unknown', label: 'Non statue', className: 'bg-slate-50 text-slate-600 border-slate-200' },
];

const EMPTY_DRAFT = {
  previous_status: '',
  next_milestone: '',
  weekly_objective: '',
  priority: '',
  notes: '',
};

const buildReviewPayload = (sourceDraft) => {
  const priority = sourceDraft.priority === '' ? null : Number(sourceDraft.priority);
  return {
    previous_status: sourceDraft.previous_status || null,
    next_milestone: (sourceDraft.next_milestone || '').trim(),
    weekly_objective: (sourceDraft.weekly_objective || '').trim(),
    priority: Number.isInteger(priority) ? priority : null,
    notes: sourceDraft.notes || '',
  };
};

const buildReviewSignature = (projectId, payload) => (
  JSON.stringify({ project_id: projectId ? Number(projectId) : null, ...(payload || {}) })
);

const mentionPattern = /@\[(circle|role|project):(\d+)\|([^\]]+)\]/g;

const formatFocusText = (text) => (
  (text || '').replace(mentionPattern, (_match, _type, _id, label) => `@${label}`)
);

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const parseDate = (value, endOfDay = false) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
  return date;
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR');
};

const formatDateRange = (start, end) => {
  if (!start && !end) return '';
  if (start && end) return `${formatDate(start)} - ${formatDate(end)}`;
  return start ? `Depuis ${formatDate(start)}` : `Jusqu'au ${formatDate(end)}`;
};

const statusLabel = (value) => {
  const option = STATUS_OPTIONS.find((item) => item.value === value);
  return option?.label || 'Non renseigne';
};

const projectStatusLabel = (status) => {
  const norm = String(status || '').toLowerCase();
  if (norm === 'in_progress') return 'En cours';
  if (norm === 'completed') return 'Termine';
  if (norm === 'proposition') return 'Proposition';
  return status || 'Statut inconnu';
};

const findCurrentOrNext = (items) => {
  const today = parseDate(new Date());
  const ordered = (items || [])
    .filter((item) => item?.start_date || item?.end_date)
    .sort((a, b) => {
      const left = parseDate(a.start_date || a.end_date)?.getTime() || 0;
      const right = parseDate(b.start_date || b.end_date)?.getTime() || 0;
      return left - right;
    });
  const active = ordered.find((item) => {
    const start = parseDate(item.start_date);
    const end = parseDate(item.end_date, true);
    if (!start || !end) return false;
    return today >= start && today <= end;
  });
  if (active) return { item: active, state: 'En cours' };
  const upcoming = ordered.find((item) => {
    const start = parseDate(item.start_date || item.end_date);
    return start && start > today;
  });
  if (upcoming) return { item: upcoming, state: 'Prochain' };
  const latest = [...ordered].reverse()[0];
  return latest ? { item: latest, state: 'Dernier' } : null;
};

const MeetingPanelPilotage = ({
  activeMeeting,
  selectedCircle,
  meetingError,
  meetingParticipants = [],
  connectedUsers = [],
  realtimeConnected,
  presenceCount,
  employees = [],
  projects = [],
  circles = [],
  projectSprints = {},
  meetingProjectReviews = [],
  selectedMeetingProjectId,
  onFetchProjectSprints,
  onFetchProjectReviews,
  onUpdateProjectReview,
  onSelectMeetingProject,
  userIsSecretary,
  onCloseMeeting,
  onTransferSecretary,
  onInviteParticipant,
}) => {
  const { getAuthHeaders } = useAuth();
  const [inviteEmployeeId, setInviteEmployeeId] = useState('');
  const [projectIterationsById, setProjectIterationsById] = useState({});
  const [iterationsLoading, setIterationsLoading] = useState(false);
  const [iterationsError, setIterationsError] = useState(null);
  const [projectDetailsById, setProjectDetailsById] = useState({});
  const [projectParticipantsById, setProjectParticipantsById] = useState({});
  const [roleFocusByParentId, setRoleFocusByParentId] = useState({});
  const [projectPeopleLoading, setProjectPeopleLoading] = useState(false);
  const [projectFocusLoading, setProjectFocusLoading] = useState(false);
  const [projectPeopleError, setProjectPeopleError] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saveState, setSaveState] = useState('idle');
  const draftRef = useRef(EMPTY_DRAFT);
  const autosaveTimerRef = useRef(null);
  const saveStateTimerRef = useRef(null);
  const autosaveRequestSeqRef = useRef(0);
  const hydratingDraftRef = useRef(false);
  const lastSavedSignatureRef = useRef('');
  const selectedProjectIdRef = useRef(null);

  const meetingCircleId = activeMeeting?.circle_id || selectedCircle?.id;

  const childrenByParent = useMemo(() => {
    const map = {};
    (circles || []).forEach((circle) => {
      if (!circle?.parent_id) return;
      if (!map[circle.parent_id]) map[circle.parent_id] = [];
      map[circle.parent_id].push(circle);
    });
    return map;
  }, [circles]);

  const circleById = useMemo(() => {
    const map = {};
    (circles || []).forEach((circle) => {
      if (circle?.id !== undefined && circle?.id !== null) map[String(circle.id)] = circle;
    });
    return map;
  }, [circles]);

  const getDescendantIds = useCallback(
    (circleId) => {
      const ids = new Set();
      const stack = [circleId];
      while (stack.length > 0) {
        const current = stack.pop();
        if (!current || ids.has(current)) continue;
        ids.add(current);
        (childrenByParent[current] || []).forEach((child) => {
          if (child?.id) stack.push(child.id);
        });
      }
      return ids;
    },
    [childrenByParent]
  );

  const meetingProjects = useMemo(() => {
    let list = projects || [];
    if (meetingCircleId) {
      const scopeIds = getDescendantIds(Number(meetingCircleId));
      list = list.filter((project) => project?.circle_id && scopeIds.has(Number(project.circle_id)));
    }
    return list
      .filter((project) => String(project?.status || '').toLowerCase() === 'in_progress')
      .sort((a, b) => Number(a.project_number || a.id || 0) - Number(b.project_number || b.id || 0));
  }, [projects, meetingCircleId, getDescendantIds]);

  const projectIdsKey = useMemo(
    () => meetingProjects.map((project) => project.id).join(','),
    [meetingProjects]
  );

  const selectedProject = useMemo(
    () => meetingProjects.find((project) => String(project.id) === String(selectedMeetingProjectId)) || null,
    [meetingProjects, selectedMeetingProjectId]
  );

  const selectedProjectDetail = selectedProject
    ? projectDetailsById[String(selectedProject.id)] || selectedProject
    : null;

  const activeEmployees = useMemo(
    () => (employees || []).filter((employee) => employee?.is_active !== false),
    [employees]
  );

  const employeeById = useMemo(() => {
    const map = {};
    (employees || []).forEach((employee) => {
      if (employee?.id !== undefined && employee?.id !== null) map[String(employee.id)] = employee;
    });
    return map;
  }, [employees]);

  const focusParentCircleId = useMemo(() => {
    const rawCircleId = selectedProjectDetail?.circle_id;
    if (rawCircleId === null || rawCircleId === undefined || rawCircleId === '') return null;
    const circleId = Number(rawCircleId);
    if (Number.isNaN(circleId)) return null;
    const circle = circleById[String(circleId)];
    const childCount = (childrenByParent[String(circleId)] || childrenByParent[circleId] || []).length;
    const isRoleCircle = circle?.parent_id !== null && circle?.parent_id !== undefined && childCount === 0;
    return isRoleCircle ? Number(circle.parent_id) : circleId;
  }, [selectedProjectDetail?.circle_id, circleById, childrenByParent]);

  const selectedProjectParticipants = useMemo(
    () => (selectedProject ? projectParticipantsById[String(selectedProject.id)] || [] : []),
    [selectedProject, projectParticipantsById]
  );

  const selectedRoleFocusEntries = useMemo(
    () => (focusParentCircleId ? roleFocusByParentId[String(focusParentCircleId)] || [] : []),
    [focusParentCircleId, roleFocusByParentId]
  );

  const reviewByProjectId = useMemo(() => {
    const map = {};
    (meetingProjectReviews || []).forEach((entry) => {
      if (entry?.project_id === undefined || entry?.project_id === null) return;
      map[String(entry.project_id)] = entry;
    });
    return map;
  }, [meetingProjectReviews]);

  const selectedReviewEntry = selectedProject ? reviewByProjectId[String(selectedProject.id)] || {} : {};
  const currentReview = selectedReviewEntry.current || null;
  const lastReview = selectedReviewEntry.last || null;

  const meetingParticipantIds = useMemo(
    () => new Set((meetingParticipants || []).map((participant) => String(participant.id))),
    [meetingParticipants]
  );

  const availableInvitees = useMemo(
    () => activeEmployees.filter((employee) => !meetingParticipantIds.has(String(employee.id))),
    [activeEmployees, meetingParticipantIds]
  );

  const isParticipantConnected = useCallback(
    (participantId) => connectedUsers.some((user) => {
      const connectedId = user?.employee_id ?? user?.user_id ?? user?.id;
      return String(connectedId) === String(participantId);
    }),
    [connectedUsers]
  );

  const secretary = meetingParticipants.find((participant) => String(participant.id) === String(activeMeeting?.created_by));

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (!meetingProjects.length || selectedProject) return;
    if (userIsSecretary && onSelectMeetingProject) {
      onSelectMeetingProject(meetingProjects[0].id);
    }
  }, [meetingProjects, selectedProject, userIsSecretary, onSelectMeetingProject]);

  useEffect(() => {
    if (!projectIdsKey || !onFetchProjectReviews) return;
    onFetchProjectReviews(meetingProjects.map((project) => project.id));
  }, [projectIdsKey, meetingProjects, onFetchProjectReviews]);

  useEffect(() => {
    if (!selectedProject?.id || !onFetchProjectSprints) return;
    onFetchProjectSprints(Number(selectedProject.id));
  }, [selectedProject?.id, onFetchProjectSprints]);

  useEffect(() => {
    if (!selectedProject?.id) return;
    const key = String(selectedProject.id);
    if (projectIterationsById[key]) return;
    let mounted = true;
    setIterationsLoading(true);
    setIterationsError(null);
    fetch(`${apiService.getBaseUrl()}/projects/${selectedProject.id}/iterations`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Impossible de charger les iterations");
        return response.json();
      })
      .then((data) => {
        if (!mounted) return;
        setProjectIterationsById((prev) => ({ ...prev, [key]: Array.isArray(data) ? data : [] }));
      })
      .catch((err) => {
        if (!mounted) return;
        setIterationsError(err.message);
        setProjectIterationsById((prev) => ({ ...prev, [key]: [] }));
      })
      .finally(() => {
        if (mounted) setIterationsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedProject?.id, projectIterationsById, getAuthHeaders]);

  useEffect(() => {
    if (!selectedProject?.id) {
      setProjectPeopleError(null);
      return undefined;
    }
    const key = String(selectedProject.id);
    const hasDetails = Boolean(projectDetailsById[key]);
    const hasParticipants = Boolean(projectParticipantsById[key]);
    if (hasDetails && hasParticipants) return undefined;

    let mounted = true;
    setProjectPeopleLoading(true);
    setProjectPeopleError(null);

    const detailRequest = hasDetails
      ? Promise.resolve(projectDetailsById[key])
      : fetch(`${apiService.getBaseUrl()}/projects/${selectedProject.id}`, {
        headers: getAuthHeaders(),
      }).then((response) => {
        if (!response.ok) throw new Error('Impossible de charger le detail du projet');
        return response.json();
      });

    const participantsRequest = hasParticipants
      ? Promise.resolve(projectParticipantsById[key])
      : fetch(`${apiService.getBaseUrl()}/projects/${selectedProject.id}/participants`, {
        headers: getAuthHeaders(),
      }).then((response) => {
        if (!response.ok) throw new Error('Impossible de charger les participants du projet');
        return response.json();
      });

    Promise.all([detailRequest, participantsRequest])
      .then(([detail, participants]) => {
        if (!mounted) return;
        setProjectDetailsById((prev) => (
          prev[key] ? prev : { ...prev, [key]: detail || selectedProject }
        ));
        setProjectParticipantsById((prev) => (
          prev[key] ? prev : { ...prev, [key]: Array.isArray(participants) ? participants : [] }
        ));
      })
      .catch((err) => {
        if (!mounted) return;
        setProjectPeopleError(err.message);
      })
      .finally(() => {
        if (mounted) setProjectPeopleLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [selectedProject, selectedProject?.id, projectDetailsById, projectParticipantsById, getAuthHeaders]);

  useEffect(() => {
    if (!focusParentCircleId) return undefined;
    const key = String(focusParentCircleId);
    if (roleFocusByParentId[key]) return undefined;

    let mounted = true;
    setProjectFocusLoading(true);
    setProjectPeopleError(null);

    fetch(`${apiService.getBaseUrl()}/circle-focus?parent_id=${focusParentCircleId}`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Impossible de charger les roles du projet');
        return response.json();
      })
      .then((data) => {
        if (!mounted) return;
        setRoleFocusByParentId((prev) => ({ ...prev, [key]: Array.isArray(data) ? data : [] }));
      })
      .catch((err) => {
        if (!mounted) return;
        setProjectPeopleError(err.message);
      })
      .finally(() => {
        if (mounted) setProjectFocusLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [focusParentCircleId, roleFocusByParentId, getAuthHeaders]);

  useEffect(() => {
    if (!selectedProject?.id) {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
      if (saveStateTimerRef.current) window.clearTimeout(saveStateTimerRef.current);
      autosaveTimerRef.current = null;
      saveStateTimerRef.current = null;
      hydratingDraftRef.current = true;
      setDraft(EMPTY_DRAFT);
      draftRef.current = EMPTY_DRAFT;
      selectedProjectIdRef.current = null;
      lastSavedSignatureRef.current = '';
      autosaveRequestSeqRef.current += 1;
      setSaveState('idle');
      const timeoutId = window.setTimeout(() => {
        hydratingDraftRef.current = false;
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }

    const nextDraft = {
      previous_status: currentReview?.previous_status || '',
      next_milestone: currentReview?.next_milestone || '',
      weekly_objective: currentReview?.weekly_objective || '',
      priority: currentReview?.priority !== null && currentReview?.priority !== undefined ? String(currentReview.priority) : '',
      notes: currentReview?.notes || '',
    };
    const projectChanged = String(selectedProjectIdRef.current ?? '') !== String(selectedProject.id);
    const localSignature = buildReviewSignature(selectedProject.id, buildReviewPayload(draftRef.current));
    const hasLocalChanges = Boolean(lastSavedSignatureRef.current && localSignature !== lastSavedSignatureRef.current);
    if (!projectChanged && userIsSecretary && hasLocalChanges) return;

    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    if (saveStateTimerRef.current) window.clearTimeout(saveStateTimerRef.current);
    autosaveTimerRef.current = null;
    saveStateTimerRef.current = null;
    autosaveRequestSeqRef.current += 1;
    hydratingDraftRef.current = true;
    selectedProjectIdRef.current = selectedProject.id;
    setDraft(nextDraft);
    draftRef.current = nextDraft;
    lastSavedSignatureRef.current = buildReviewSignature(selectedProject.id, buildReviewPayload(nextDraft));
    setSaveState('idle');
    const timeoutId = window.setTimeout(() => {
      hydratingDraftRef.current = false;
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [
    selectedProject?.id,
    currentReview?.id,
    currentReview?.previous_status,
    currentReview?.next_milestone,
    currentReview?.weekly_objective,
    currentReview?.priority,
    currentReview?.notes,
    userIsSecretary,
  ]);

  const projectIterations = useMemo(
    () => (selectedProject ? projectIterationsById[String(selectedProject.id)] || [] : []),
    [selectedProject, projectIterationsById]
  );
  const projectSprintList = useMemo(
    () => (selectedProject ? projectSprints[String(selectedProject.id)] || [] : []),
    [selectedProject, projectSprints]
  );
  const currentIterationInfo = useMemo(() => findCurrentOrNext(projectIterations), [projectIterations]);
  const currentSprintInfo = useMemo(() => {
    const nested = projectIterations.flatMap((iteration) =>
      (iteration?.sprints || []).map((sprint) => ({
        ...sprint,
        iteration_name: iteration.name,
        iteration_id: sprint.iteration_id || iteration.id,
      }))
    );
    return findCurrentOrNext(nested.length > 0 ? nested : projectSprintList);
  }, [projectIterations, projectSprintList]);

  const displayedMilestone = useMemo(() => {
    const parts = [];
    if (currentIterationInfo?.item) {
      const iteration = currentIterationInfo.item;
      parts.push(`${currentIterationInfo.state} iteration: ${iteration.name || `#${iteration.id}`}`);
    }
    if (currentSprintInfo?.item) {
      const sprint = currentSprintInfo.item;
      const iterationPart = sprint.iteration_name ? ` (${sprint.iteration_name})` : sprint.iteration_id ? ` (iteration #${sprint.iteration_id})` : '';
      parts.push(`${currentSprintInfo.state} sprint: ${sprint.description || `#${sprint.id}`}${iterationPart}`);
    }
    return parts.join(' / ');
  }, [currentIterationInfo, currentSprintInfo]);

  const previousPriority = getPriorityInfo(lastReview?.priority);
  const currentPriority = getPriorityInfo(draft.priority);
  const projectRoleGroups = useMemo(() => {
    const projectToken = selectedProject?.id ? `@[project:${selectedProject.id}|` : null;
    if (!projectToken) return [];
    const grouped = new Map();
    (selectedRoleFocusEntries || []).forEach((entry) => {
      if (!entry?.focus_text || !entry.focus_text.includes(projectToken)) return;
      const role = circleById[String(entry.circle_id)];
      const employee = employeeById[String(entry.employee_id)] || {
        id: entry.employee_id,
        name: `Collaborateur #${entry.employee_id}`,
        picture: null,
      };
      const member = {
        key: `${entry.circle_id}:${entry.employee_id}`,
        employeeId: entry.employee_id,
        employee,
        focusText: formatFocusText(entry.focus_text || ''),
      };
      if (!grouped.has(entry.circle_id)) {
        grouped.set(entry.circle_id, {
          roleId: entry.circle_id,
          roleName: role?.name || `Role #${entry.circle_id}`,
          members: [member],
        });
      } else {
        grouped.get(entry.circle_id).members.push(member);
      }
    });
    const groups = Array.from(grouped.values());
    groups.forEach((group) => {
      group.members.sort((a, b) => (a.employee?.name || '').localeCompare(b.employee?.name || ''));
    });
    return groups.sort((a, b) => a.roleName.localeCompare(b.roleName));
  }, [selectedProject?.id, selectedRoleFocusEntries, circleById, employeeById]);

  const focusedEmployeeIds = useMemo(() => {
    const ids = new Set();
    projectRoleGroups.forEach((group) => {
      group.members.forEach((member) => {
        if (member.employeeId !== undefined && member.employeeId !== null) ids.add(String(member.employeeId));
      });
    });
    return ids;
  }, [projectRoleGroups]);

  const standaloneProjectParticipants = useMemo(() => {
    return (selectedProjectParticipants || [])
      .filter((participant) => participant?.id !== undefined && participant?.id !== null)
      .filter((participant) => !focusedEmployeeIds.has(String(participant.id)))
      .map((participant) => ({
        ...participant,
        employee: employeeById[String(participant.id)] || participant,
      }))
      .sort((a, b) => (a.employee?.name || a.name || '').localeCompare(b.employee?.name || b.name || ''));
  }, [selectedProjectParticipants, focusedEmployeeIds, employeeById]);

  const projectPeopleCount = useMemo(() => {
    const ids = new Set(focusedEmployeeIds);
    standaloneProjectParticipants.forEach((participant) => {
      if (participant?.id !== undefined && participant?.id !== null) ids.add(String(participant.id));
    });
    return ids.size;
  }, [focusedEmployeeIds, standaloneProjectParticipants]);
  const projectPeopleIsLoading = projectPeopleLoading || projectFocusLoading;

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const reviewPayload = useMemo(() => buildReviewPayload(draft), [draft]);
  const reviewSignature = useMemo(
    () => (selectedProject?.id ? buildReviewSignature(selectedProject.id, reviewPayload) : ''),
    [selectedProject?.id, reviewPayload]
  );

  useEffect(() => {
    if (!selectedProject?.id || !userIsSecretary || !onUpdateProjectReview) return undefined;
    if (hydratingDraftRef.current || !reviewSignature) return undefined;
    if (lastSavedSignatureRef.current === reviewSignature) return undefined;

    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    if (saveStateTimerRef.current) window.clearTimeout(saveStateTimerRef.current);
    saveStateTimerRef.current = null;
    setSaveState('pending');

    autosaveTimerRef.current = window.setTimeout(async () => {
      autosaveTimerRef.current = null;
      const requestSeq = autosaveRequestSeqRef.current + 1;
      const requestSignature = reviewSignature;
      autosaveRequestSeqRef.current = requestSeq;
      setSaveState('saving');
      try {
        const updated = await onUpdateProjectReview(selectedProject.id, reviewPayload);
        const localSignature = buildReviewSignature(selectedProject.id, buildReviewPayload(draftRef.current));
        if (autosaveRequestSeqRef.current !== requestSeq) return;
        if (!updated) {
          if (localSignature === requestSignature) setSaveState('error');
          return;
        }
        lastSavedSignatureRef.current = requestSignature;
        if (localSignature === requestSignature) {
          setSaveState('saved');
          saveStateTimerRef.current = window.setTimeout(() => setSaveState('idle'), 1600);
        }
      } catch (err) {
        const localSignature = buildReviewSignature(selectedProject.id, buildReviewPayload(draftRef.current));
        if (autosaveRequestSeqRef.current === requestSeq && localSignature === requestSignature) {
          setSaveState('error');
        }
      }
    }, 650);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [selectedProject?.id, userIsSecretary, onUpdateProjectReview, reviewPayload, reviewSignature]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    if (saveStateTimerRef.current) window.clearTimeout(saveStateTimerRef.current);
  }, []);

  const saveStateLabel = useMemo(() => {
    if (saveState === 'pending') return "Enregistrement en attente...";
    if (saveState === 'saving') return "Enregistrement...";
    if (saveState === 'saved') return "Enregistre automatiquement";
    if (saveState === 'error') return "Erreur d'enregistrement";
    return "Enregistrement automatique";
  }, [saveState]);

  const saveStateClassName = useMemo(() => {
    if (saveState === 'error') return 'text-rose-500';
    if (saveState === 'saved') return 'text-emerald-600';
    if (saveState === 'saving' || saveState === 'pending') return 'text-amber-600';
    return 'text-slate-400';
  }, [saveState]);

  const reviewedCount = meetingProjects.filter((project) => {
    const current = reviewByProjectId[String(project.id)]?.current;
    return Boolean(
      current?.weekly_objective ||
      current?.previous_status ||
      (current?.priority !== null && current?.priority !== undefined)
    );
  }).length;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md h-auto min-h-full overflow-visible flex flex-col">
      <div className="flex items-start justify-between gap-4 mb-6 border-b border-slate-100 pb-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">
            <RefreshCcw size={12} /> Reunion de pilotage
          </div>
          <h2 className="text-2xl font-bold text-slate-900">
            {selectedCircle?.name || 'Reunion'}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Leader de reunion : {secretary?.name || 'Non defini'}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Rythme : lundi matin
          </p>
        </div>

        <div className="flex items-start gap-3">
          <div className="hidden md:flex flex-col gap-2">
            <div className="flex flex-wrap justify-end gap-2 max-w-xl">
              {meetingParticipants.map((participant, index) => {
                const isConnected = isParticipantConnected(participant.id);
                const employee = employeeById[String(participant.id)];
                const avatarUrl = employee?.picture || employee?.photo_url || employee?.avatar_url;
                const isSecretary = String(participant.id) === String(activeMeeting?.created_by);
                const canTransfer = userIsSecretary && !isSecretary && isConnected && Boolean(onTransferSecretary);
                return (
                  <button
                    key={`${participant.id || index}`}
                    type="button"
                    onClick={() => canTransfer && onTransferSecretary(participant.id)}
                    title={canTransfer ? 'Transferer le lead de reunion' : participant.name}
                    className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full text-xs font-medium border ${isConnected
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-slate-50 border-slate-200 text-slate-500 opacity-70'
                      } ${canTransfer ? 'cursor-pointer hover:bg-cyan-50 hover:border-cyan-200' : 'cursor-default'}`}
                  >
                    <span className="w-5 h-5 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center text-[9px] font-bold uppercase">
                      {avatarUrl ? <img src={avatarUrl} alt={participant.name} className="w-full h-full object-cover" /> : participant.name?.charAt(0)}
                    </span>
                    <span>{participant.name?.split(' ')[0] || 'Membre'}</span>
                  </button>
                );
              })}
            </div>
            {userIsSecretary && onInviteParticipant && (
              <div className="flex justify-end gap-2">
                <select
                  value={inviteEmployeeId}
                  onChange={(event) => setInviteEmployeeId(event.target.value)}
                  className="min-w-[180px] border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-emerald-100 outline-none"
                  disabled={availableInvitees.length === 0}
                >
                  <option value="">
                    {availableInvitees.length === 0 ? 'Aucune personne a inviter' : 'Inviter une personne'}
                  </option>
                  {availableInvitees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    if (!inviteEmployeeId) return;
                    onInviteParticipant(Number(inviteEmployeeId));
                    setInviteEmployeeId('');
                  }}
                  className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  disabled={!inviteEmployeeId}
                >
                  Inviter
                </button>
              </div>
            )}
          </div>

          <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${realtimeConnected ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
            <div className={`w-2 h-2 rounded-full ${realtimeConnected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
            {realtimeConnected ? 'En ligne' : 'Hors ligne'}
            {presenceCount > 0 && <span className="opacity-75">({presenceCount})</span>}
          </div>

          {userIsSecretary && (
            <button
              onClick={onCloseMeeting}
              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors"
            >
              Cloturer
            </button>
          )}
        </div>
      </div>

      {meetingError && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
          <AlertCircle size={16} />
          {meetingError}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6 h-full overflow-visible">
        <div className="w-full lg:w-96 shrink-0 flex flex-col gap-4 h-full overflow-y-auto pr-2">
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Projets du cercle</h3>
              <span className="text-[10px] text-slate-400">{reviewedCount}/{meetingProjects.length}</span>
            </div>
            {meetingProjects.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Aucun projet en cours.</p>
            ) : (
              <div className="space-y-2 max-h-[62vh] overflow-y-auto">
                {meetingProjects.map((project) => {
                  const isSelected = String(selectedMeetingProjectId) === String(project.id);
                  const entry = reviewByProjectId[String(project.id)] || {};
                  const current = entry.current || {};
                  const priority = getPriorityInfo(current.priority ?? entry.last?.priority);
                  const hasReview = Boolean(
                    current.weekly_objective ||
                    current.previous_status ||
                    (current.priority !== null && current.priority !== undefined)
                  );
                  return (
                    <button
                      key={project.id}
                      type="button"
                      onClick={() => {
                        if (!userIsSecretary || !onSelectMeetingProject) return;
                        onSelectMeetingProject(project.id);
                      }}
                      className={`w-full text-left p-3 rounded-lg border text-xs transition-colors ${isSelected
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-200'
                        } ${userIsSecretary ? 'cursor-pointer' : 'cursor-default'}`}
                      disabled={!userIsSecretary}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{project.name}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{projectStatusLabel(project.status)}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {hasReview && <CheckCircle2 size={14} className="text-emerald-500" />}
                          {project.project_number !== undefined && project.project_number !== null && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">
                              #{project.project_number}
                            </span>
                          )}
                        </div>
                      </div>
                      {priority && (
                        <span className={`mt-2 inline-flex text-[10px] px-2 py-0.5 rounded-full border font-semibold ${priority.className}`}>
                          P{priority.value} - {priority.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {!userIsSecretary && meetingProjects.length > 0 && (
              <p className="text-[10px] text-slate-400">Le leader de reunion choisit le projet affiche.</p>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0 bg-slate-50/60 rounded-xl border border-slate-200 p-5">
          {!selectedProject ? (
            <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-slate-400">
              <Clock3 size={28} className="mb-3 text-slate-300" />
              <p>Selectionnez un projet pour demarrer la revue.</p>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto space-y-5">
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Projet</p>
                    <h3 className="text-2xl font-bold text-slate-900 mt-1">{selectedProject.name}</h3>
                    <div className="flex flex-wrap gap-2 mt-3 text-xs text-slate-500">
                      {selectedProject.project_number !== undefined && selectedProject.project_number !== null && (
                        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">#{selectedProject.project_number}</span>
                      )}
                      <span className="px-2 py-1 rounded-full bg-white border border-slate-200 flex items-center gap-1.5">
                        <Users size={12} />
                        {projectPeopleCount} participant{projectPeopleCount > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {previousPriority && (
                      <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${previousPriority.className}`}>
                        Priorite precedente : P{previousPriority.value} - {previousPriority.label}
                      </span>
                    )}
                    {currentPriority && (
                      <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${currentPriority.className}`}>
                        Priorite saisie : P{currentPriority.value} - {currentPriority.label}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-5 border-t border-slate-100 pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
                      <Users size={14} className="text-emerald-600" />
                      Participants et roles projet
                    </div>
                    {projectPeopleIsLoading && <span className="text-[10px] text-slate-400">Chargement...</span>}
                  </div>

                  {projectPeopleError && (
                    <p className="mt-3 text-xs text-rose-500">{projectPeopleError}</p>
                  )}

                  {projectPeopleIsLoading && projectPeopleCount === 0 ? (
                    <p className="mt-3 text-sm text-slate-400 italic">Chargement des participants du projet...</p>
                  ) : !projectPeopleIsLoading && projectPeopleCount === 0 ? (
                    <p className="mt-3 text-sm text-slate-400 italic">Aucun participant ou focus de role trouve pour ce projet.</p>
                  ) : (
                    <div className="mt-3 grid gap-3 lg:grid-cols-2">
                      {projectRoleGroups.map((role) => (
                        <div key={role.roleId} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-slate-800">{role.roleName}</p>
                            <span className="text-[10px] font-semibold text-slate-400">
                              {role.members.length} pers.
                            </span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {role.members.map((member) => {
                              const picture = member.employee?.picture || member.employee?.photo_url || member.employee?.avatar_url;
                              const name = member.employee?.name || 'Collaborateur';
                              return (
                                <div
                                  key={member.key}
                                  className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2"
                                  title={member.focusText}
                                >
                                  <span className="h-7 w-7 shrink-0 overflow-hidden rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
                                    {picture ? <img src={picture} alt={name} className="h-full w-full object-cover" /> : getInitials(name)}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="truncate text-xs font-semibold text-slate-800">{name}</p>
                                    {member.focusText && (
                                      <p className="max-w-[220px] truncate text-[10px] text-slate-400">{member.focusText}</p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      {standaloneProjectParticipants.length > 0 && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-slate-800">Participants projet</p>
                            <span className="text-[10px] font-semibold text-slate-400">
                              {standaloneProjectParticipants.length} pers.
                            </span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {standaloneProjectParticipants.map((participant) => {
                              const employee = participant.employee || participant;
                              const picture = employee.picture || employee.photo_url || employee.avatar_url || participant.picture;
                              const name = employee.name || participant.name || 'Collaborateur';
                              return (
                                <div key={participant.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                                  <span className="h-7 w-7 shrink-0 overflow-hidden rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
                                    {picture ? <img src={picture} alt={name} className="h-full w-full object-cover" /> : getInitials(name)}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="truncate text-xs font-semibold text-slate-800">{name}</p>
                                    <p className="text-[10px] text-slate-400">Participant projet</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div className="bg-white p-5 rounded-xl border border-amber-100 shadow-sm space-y-4">
                  <div>
                    <div className="flex items-center gap-2 text-xs font-bold text-amber-700 uppercase tracking-wider">
                      <Target size={14} />
                      Dernier objectif fixe
                    </div>
                    {lastReview?.weekly_objective ? (
                      <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50/70 p-4 min-h-[104px]">
                        <p className="text-base font-semibold leading-relaxed text-slate-900 whitespace-pre-wrap">{lastReview.weekly_objective}</p>
                      </div>
                    ) : (
                      <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-4 min-h-[104px] flex items-center">
                        <p className="text-sm text-slate-400 italic">Aucun objectif precedent trouve.</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Resultat annonce par le projet</p>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((option) => {
                        const active = draft.previous_status === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => userIsSecretary && updateDraft('previous_status', option.value)}
                            disabled={!userIsSecretary}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${active ? option.className : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'} disabled:opacity-70`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    {!userIsSecretary && (
                      <p className="text-xs text-slate-400 mt-2">Resultat : {statusLabel(currentReview?.previous_status)}</p>
                    )}
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-cyan-100 shadow-sm space-y-4">
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-xs font-bold text-cyan-700 uppercase tracking-wider">
                        <CalendarDays size={14} />
                        Prochain jalon
                      </div>
                      {iterationsLoading && <span className="text-[10px] text-slate-400">Chargement...</span>}
                    </div>
                    {iterationsError ? (
                      <p className="mt-2 text-xs text-rose-500">{iterationsError}</p>
                    ) : displayedMilestone ? (
                      <div className="mt-3 rounded-lg border border-cyan-100 bg-cyan-50/70 p-4">
                        <p className="text-base font-semibold leading-relaxed text-slate-900">{displayedMilestone}</p>
                      </div>
                    ) : (
                      <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-4">
                        <p className="text-sm text-slate-400 italic">Aucun sprint ou iteration trouve.</p>
                      </div>
                    )}
                    <div className="grid gap-2 sm:grid-cols-2 mt-3 text-xs text-slate-500">
                      {currentIterationInfo?.item && (
                        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                          <p className="font-semibold text-slate-700">{currentIterationInfo.state} iteration</p>
                          <p className="mt-1">{currentIterationInfo.item.name || `#${currentIterationInfo.item.id}`}</p>
                          <p className="mt-1 text-[11px]">{formatDateRange(currentIterationInfo.item.start_date, currentIterationInfo.item.end_date)}</p>
                        </div>
                      )}
                      {currentSprintInfo?.item && (
                        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                          <p className="font-semibold text-slate-700">{currentSprintInfo.state} sprint</p>
                          <p className="mt-1">{currentSprintInfo.item.description || `#${currentSprintInfo.item.id}`}</p>
                          <p className="mt-1 text-[11px]">{formatDateRange(currentSprintInfo.item.start_date, currentSprintInfo.item.end_date)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <label className="block">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Jalon retenu</span>
                    <input
                      type="text"
                      value={draft.next_milestone}
                      onChange={(event) => updateDraft('next_milestone', event.target.value)}
                      disabled={!userIsSecretary}
                      placeholder={displayedMilestone || 'Prochain jalon du projet'}
                      className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-100 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                    />
                  </label>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-emerald-100 shadow-sm space-y-4">
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px] gap-5">
                  <label className="block">
                    <span className="flex items-center gap-2 text-xs font-bold text-emerald-700 uppercase tracking-wider">
                      <Flag size={14} />
                      Objectif de la semaine
                    </span>
                    <textarea
                      value={draft.weekly_objective}
                      onChange={(event) => updateDraft('weekly_objective', event.target.value)}
                      disabled={!userIsSecretary}
                      placeholder="Objectif clair a atteindre avant la prochaine reunion..."
                      className="mt-3 w-full border border-emerald-100 rounded-lg px-4 py-3 text-sm min-h-[160px] bg-emerald-50/30 focus:ring-2 focus:ring-emerald-100 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                    />
                  </label>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Priorite de la semaine</span>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {PRIORITY_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => userIsSecretary && updateDraft('priority', String(option.value))}
                          disabled={!userIsSecretary}
                          className={`text-[10px] px-2 py-1 rounded-full border font-semibold ${String(draft.priority) === String(option.value) ? option.className : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'} disabled:opacity-70`}
                        >
                          P{option.value} {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <label className="block">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Notes</span>
                  <textarea
                    value={draft.notes}
                    onChange={(event) => updateDraft('notes', event.target.value)}
                    disabled={!userIsSecretary}
                    placeholder="Notes de decision, arbitrages, blocages..."
                    className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[90px] focus:ring-2 focus:ring-emerald-100 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                  />
                </label>

                {userIsSecretary ? (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className={`text-xs font-medium ${saveStateClassName}`}>
                      {saveStateLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateDraft('next_milestone', displayedMilestone)}
                      className="px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                      disabled={!displayedMilestone}
                    >
                      Utiliser le jalon affiche
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Le leader de reunion saisit le suivi de pilotage.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingPanelPilotage;
